import { useEffect } from 'react';

interface CriticalCSSProps {
  styles: string;
}

export function CriticalCSS({ styles }: CriticalCSSProps) {
  useEffect(() => {
    // Inject critical CSS into the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    styleElement.setAttribute('data-critical-css', 'true');
    document.head.appendChild(styleElement);

    // Cleanup function to remove the style element when component unmounts
    return () => {
      const criticalStyles = document.querySelector('[data-critical-css="true"]');
      if (criticalStyles) {
        criticalStyles.remove();
      }
    };
  }, [styles]);

  return null; // This component doesn't render anything visible
}

// Critical CSS for above-the-fold content
export const criticalStyles = `
  /* Reset and base styles */
  *, *::before, *::after {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  /* Critical layout styles */
  .min-h-screen {
    min-height: 100vh;
  }
  
  .bg-gradient-to-br {
    background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
  }
  
  .from-blue-900 {
    --tw-gradient-from: #1e3a8a;
    --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(30, 58, 138, 0));
  }
  
  .via-blue-800 {
    --tw-gradient-stops: var(--tw-gradient-from), #1e40af, var(--tw-gradient-to, rgba(30, 64, 175, 0));
  }
  
  .to-blue-700 {
    --tw-gradient-to: #1d4ed8;
  }
  
  /* Navigation critical styles */
  .sticky {
    position: sticky;
  }
  
  .top-0 {
    top: 0;
  }
  
  .z-50 {
    z-index: 50;
  }
  
  .bg-white {
    background-color: #ffffff;
  }
  
  .shadow-lg {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
  
  /* Container and layout */
  .container {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  @media (min-width: 640px) {
    .container {
      max-width: 640px;
    }
  }
  
  @media (min-width: 768px) {
    .container {
      max-width: 768px;
    }
  }
  
  @media (min-width: 1024px) {
    .container {
      max-width: 1024px;
    }
  }
  
  @media (min-width: 1280px) {
    .container {
      max-width: 1280px;
    }
  }
  
  /* Typography critical styles */
  .text-4xl {
    font-size: 2.25rem;
    line-height: 2.5rem;
  }
  
  .text-5xl {
    font-size: 3rem;
    line-height: 1;
  }
  
  .font-bold {
    font-weight: 700;
  }
  
  .text-white {
    color: #ffffff;
  }
  
  .text-center {
    text-align: center;
  }
  
  /* Flexbox critical styles */
  .flex {
    display: flex;
  }
  
  .items-center {
    align-items: center;
  }
  
  .justify-center {
    justify-content: center;
  }
  
  .justify-between {
    justify-content: space-between;
  }
  
  /* Spacing critical styles */
  .py-6 {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
  
  .py-20 {
    padding-top: 5rem;
    padding-bottom: 5rem;
  }
  
  .px-4 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .mb-6 {
    margin-bottom: 1.5rem;
  }
  
  .mb-16 {
    margin-bottom: 4rem;
  }
  
  /* Loading spinner */
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  .rounded-full {
    border-radius: 9999px;
  }
  
  .h-12 {
    height: 3rem;
  }
  
  .w-12 {
    width: 3rem;
  }
  
  .border-b-2 {
    border-bottom-width: 2px;
  }
  
  .border-blue-600 {
    border-color: #2563eb;
  }
  
  .border-white {
    border-color: #ffffff;
  }
  
  /* Responsive utilities */
  @media (min-width: 768px) {
    .md\\:text-5xl {
      font-size: 3rem;
      line-height: 1;
    }
  }
`;

export default CriticalCSS;